import { z } from "zod";
import { trimToNull } from "@/lib/content-form";
import { isYouTubeUrl, parseYouTubeId, youtubeThumbnailUrl } from "@/lib/youtube";

export const HOF_MEDIA_TYPES = ["image", "video"] as const;
export type HofMediaType = (typeof HOF_MEDIA_TYPES)[number];

const mediaUrlSchema = z
  .string()
  .trim()
  .min(1)
  .max(2000)
  .refine((value) => {
    if (isYouTubeUrl(value)) return true;
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }, "Media must be a valid image/video URL or YouTube link.");

export const hofEntryInput = z
  .object({
    title: z.string().trim().min(1).max(200),
    subtitle: z.string().trim().max(200).optional().nullable().transform(trimToNull),
    body: z.string().max(10000).optional().default(""),
    entry_year: z.number().int().min(1900).max(2100),
    media_type: z.enum(HOF_MEDIA_TYPES),
    media_url: mediaUrlSchema,
    published: z.boolean().optional(),
    sort_order: z.number().int().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.media_type === "image" && isYouTubeUrl(data.media_url)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Image entries cannot use a YouTube URL. Upload an image instead.",
        path: ["media_url"],
      });
    }
  });

/** Unit-testable parse of Hall of Fame create/update input (no Supabase). */
export function parseHofEntryInput(data: unknown) {
  return hofEntryInput.parse(data);
}

export const HOF_MEDIA_BUCKET = "hall-of-fame-media";

export const HOF_DECADES = [
  { id: "1980s", label: "1980s", start: 1980, end: 1989 },
  { id: "1990s", label: "1990s", start: 1990, end: 1999 },
  { id: "2000s", label: "2000s", start: 2000, end: 2009 },
  { id: "2010s", label: "2010s", start: 2010, end: 2019 },
  { id: "today", label: "Today", start: 2020, end: 2100 },
] as const;

export type HofDecadeId = (typeof HOF_DECADES)[number]["id"];

export function decadeForYear(year: number): HofDecadeId | null {
  const decade = HOF_DECADES.find((d) => year >= d.start && year <= d.end);
  return decade?.id ?? null;
}

export function entriesInDecade<T extends { entry_year: number }>(
  entries: T[],
  decadeId: HofDecadeId,
): T[] {
  const decade = HOF_DECADES.find((d) => d.id === decadeId);
  if (!decade) return [];
  return entries.filter((e) => e.entry_year >= decade.start && e.entry_year <= decade.end);
}

export function decadesWithEntries(entries: { entry_year: number }[]): HofDecadeId[] {
  const present = new Set<HofDecadeId>();
  for (const entry of entries) {
    const id = decadeForYear(entry.entry_year);
    if (id) present.add(id);
  }
  return HOF_DECADES.map((d) => d.id).filter((id) => present.has(id));
}

/** Preview still for tiles: YouTube thumbnail, otherwise the media URL (image or video file). */
export function hofPreviewUrl(mediaType: HofMediaType, mediaUrl: string): string {
  if (mediaType === "video") {
    const id = parseYouTubeId(mediaUrl);
    if (id) return youtubeThumbnailUrl(id);
  }
  return mediaUrl;
}

export function isHofYouTubeMedia(mediaType: HofMediaType, mediaUrl: string): boolean {
  return mediaType === "video" && isYouTubeUrl(mediaUrl);
}

export function isHofUploadedVideo(mediaType: HofMediaType, mediaUrl: string): boolean {
  return mediaType === "video" && !isYouTubeUrl(mediaUrl);
}
